import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';

const LeadPriceSelection: React.FC = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();
  
  // Estado para el precio del lead
  const [leadPrice, setLeadPrice] = useState<number>(companyData.leadPrice || 0);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);

  // Verificar si las ubicaciones están seleccionadas, si no, redirigir
  useEffect(() => {
    if (!companyData.locations || companyData.locations.length === 0) {
      navigate('/onboarding/locations');
    }
  }, [companyData.locations, navigate]);

  // Validar el precio del lead
  const validateLeadPrice = (price: number): boolean => {
    if (price <= 0) {
      setError('El precio del lead no puede ser cero o negativo');
      return false;
    }
    if (price > 200000) {
      setError('El precio del lead no puede ser mayor a $200,000');
      return false;
    }
    setError(null);
    return true;
  };

  // Manejar cambios en el precio del lead
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setLeadPrice(isNaN(value) ? 0 : value);
    setTouched(true);
    if (touched) {
      validateLeadPrice(isNaN(value) ? 0 : value);
    }
  };

  const handleBack = () => {
    setCurrentStep(6);
    navigate('/onboarding/locations');
  };

  // Manejar el envío del formulario
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateLeadPrice(leadPrice)) {
      return;
    }
    
    // Actualizar los datos en el contexto
    updateCompanyData({
      leadPrice
    });
    
    setCurrentStep(8); // Avanzar al siguiente paso
    navigate('/onboarding/terms'); // Navegar a la página de términos y condiciones
  };

  // Formatear el precio como moneda
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <OnboardingLayout
      title="¿Cuánto valoras cada lead?"
      subtitle="Define el precio que estás dispuesto a pagar por cada lead que te enviemos. Este valor debe estar entre $2000 y $200,000 pesos."
      showBackButton={true}
      onBack={handleBack}
    >
      <div className="max-w-md mx-auto mt-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="leadPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Precio por lead (COP)
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="leadPrice"
                id="leadPrice"
                className={`block w-full pl-7 pr-12 py-3 rounded-md focus:ring-2 focus:outline-none ${
                  error
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-xervix-primary focus:border-xervix-primary'
                } dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                placeholder="0"
                value={leadPrice || ''}
                onChange={handlePriceChange}
                onBlur={() => {
                  setTouched(true);
                  validateLeadPrice(leadPrice);
                }}
                min="1"
                max="200000"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">COP</span>
              </div>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-500">{error}</p>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Resumen
            </h3>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 dark:text-gray-300">Precio por lead:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatCurrency(leadPrice || 0)}
              </span>
            </div>
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              <p>Este es el valor que pagarás por cada lead que te enviemos. Recuerda que un lead de calidad puede convertirse en un cliente recurrente.</p>
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <CancelButton />
            <button
              type="submit"
              disabled={!leadPrice || leadPrice <= 0 || leadPrice > 200000}
              className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                !leadPrice || leadPrice <= 0 || leadPrice > 200000
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-xervix-primary text-white hover:bg-opacity-90'
              }`}
            >
              Continuar
            </button>
          </div>
        </form>
      </div>
    </OnboardingLayout>
  );
};

export default LeadPriceSelection;
